import { Injectable } from '@angular/core';
import { MessageTypeE } from '@enums/message.enum';
import { MessageI } from '@interfaces/message.interface';
import { PageI } from '@interfaces/page.interface';
import { ComponentStore } from '@ngrx/component-store';
import { MessageTypeT } from '@omnitypes/message.type';
import { InstagramService } from '@services/instagram/instagram.service';
import { Observable, firstValueFrom, map } from 'rxjs';

export interface MediaMessagesStoreState {
  messages: { [chatId: string]: PageI<MessageI> };
}

@Injectable({
  providedIn: 'root',
})
export class MediaMessagesStoreService extends ComponentStore<MediaMessagesStoreState> {
  public messages$: Observable<{ [id: string]: PageI<MessageI> }> = this.select((state) => state.messages);

  constructor(private instagramService: InstagramService) {
    super({
      messages: {},
    });
  }

  public getMessagesByChatId(chatId: string): Observable<PageI<MessageI>> {
    return this.messages$.pipe(map((messages) => messages[chatId]));
  }

  public getMessageById(groupId: string, id: string) {
    return this.messages$.pipe(map((messages) => messages[groupId]));
  }

  public async populateStore(chatId: string, page: PageI<MessageI>) {
    const messages = await Promise.all(
      page.data.map(async (message) => {
        if (['ig_story', 'ig_reel'].includes(message.type)) {
          const url = message.content.ig_story?.story_media_url ?? message.content.ig_reel!.url;

          if (!(await this.checkIgMedia(url))) {
            return;
          }
        }

        return message;
      })
    );

    page.data = messages.filter((message) => !!message) as MessageI[];

    this.setState((state) => ({
      ...state,
      messages: {
        ...state.messages,
        [chatId]: page,
      },
    }));
  }

  public unshiftMessagesByChatId(chatId: string, page: PageI<MessageI>) {
    this.setState((state) => ({
      ...state,
      messages: {
        ...state.messages,
        [chatId]: {
          data: [...page.data, ...state.messages[chatId].data],
          meta: page.meta,
        },
      },
    }));
  }

  public pushMessageIfIsMedia(chatId: string, message: MessageI) {
    if (['audio', 'video', 'image'].includes(this.getMessageType(message))) {
      this.setState((state) => {
        if (state.messages[chatId]?.data?.filter((existentMessage) => existentMessage._id == message._id).length == 0) {
          return {
            ...state,
            messages: {
              ...state.messages,
              [chatId]: {
                ...state.messages[chatId],
                data: [...state.messages[chatId].data, message],
              },
            },
          };
        }
        return state;
      });
    }
  }

  public pushMessagesByChatId(chatId: string, page: PageI<MessageI>) {
    this.setState((state) => ({
      ...state,
      messages: {
        ...state.messages,
        [chatId]: {
          data: [...state.messages[chatId].data, ...page.data],
          meta: page.meta,
        },
      },
    }));
  }

  public getMessageType(message?: MessageI): MessageTypeT {
    if (['audio', 'video', 'image'].includes(message?.type || '')) {
      return message?.type as MessageTypeT;
    }

    if (message?.content?.file) {
      let fileName = message.content.file?.fileName || message.content.file?.Key;
      let format = fileName?.split('.').pop()?.toLowerCase();

      if (['mp4', 'mov', 'avi'].includes(format!)) {
        return MessageTypeE.VIDEO;
      }

      if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(format!)) {
        return MessageTypeE.IMAGE;
      }

      if (['wav', 'aac', 'ogg', 'mp3', 'm4a'].includes(format!)) {
        return MessageTypeE.AUDIO;
      }
    }

    return message?.type ?? MessageTypeE.TEXT;
  }

  private async checkIgMedia(url: string) {
    try {
      await firstValueFrom(this.instagramService.checkMedia(url));
    } catch (error) {
      return false;
    }

    return true;
  }
}
